import React, { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { useLazyQuery } from '@apollo/client';
import CITIESMODAL from '../api/citiesSearch';

import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import { useMediaQuery } from '../hooks/mediaQuery';
const Link = dynamic(() => import('next/link'));

interface CITY {
  id: string;
  name: string;
  slug: string;
  card_media: [
    {
      url: string;
      alt: string;
    }
  ];
}
const SearchModal = () => {
  const [getData, { data, loading }] = useLazyQuery(CITIESMODAL);
  const [showModal, setShowModal] = useState<boolean>(false);
  const router = useRouter();
  const [filteredCards, setFilteredCards] = useState<CITY[]>([]);
  const [searchItem, setSearchItem] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const isNotDesktop = useMediaQuery(1279);
  const isMobile = useMediaQuery(767);
  const defaultCities: CITY[] = [
    {
      id: '1',
      name: 'Niagara Falls, Canada',
      slug: 'niagara-falls-tours-canada',
      card_media: [
        {
          url: 'https://res.cloudinary.com/see-sight-tours/image/upload/v1681456351/strapi/Niagra_Falls_Canada_06610840a9.jpg',
          alt: 'Niagara Falls, Canada'
        }
      ]
    },
    {
      id: '2',
      name: 'Halifax',
      slug: 'halifax-tours',
      card_media: [
        {
          url: 'https://res.cloudinary.com/see-sight-tours/image/upload/v1638292468/strapi/halifax_night_0968e0a115.jpg',
          alt: 'Halifax'
        }
      ]
    },
    {
      id: '3',
      name: 'Toronto',
      slug: 'toronto-tours',
      card_media: [
        {
          url: 'https://res.cloudinary.com/see-sight-tours/image/upload/v1681456539/strapi/Toronto_1bc190f4a8.jpg',
          alt: 'Toronto'
        }
      ]
    }
  ];
  const handleToggleModal = () => {
    setShowModal(prev => !prev);
  };
  useEffect(() => {
    if (!showModal && searchItem?.trim() !== '') {
      setShowModal(true);
    }
  }, [searchItem]);
  useEffect(() => {
    if (!showModal) return;

    const trimmedSearch = searchItem?.trim().toLowerCase();

    if (!trimmedSearch) {
      if (isNotDesktop) {
        getData();
      } else {
        setFilteredCards(defaultCities);
      }
      return;
    }

    if (!data?.findYourCity?.cities) {
      getData();
      return;
    }

    const filtered = data.findYourCity.cities
      .map((item: any) => {
        const name = item?.city?.name?.toLowerCase();
        if (name?.includes(trimmedSearch)) {
          return item.city;
        }
        return null;
      })
      .filter((item: any) => item !== null)
      .slice(0, isNotDesktop ? data.findYourCity.cities.length : 6);

    setFilteredCards(filtered);
  }, [searchItem, showModal, data]);

  useEffect(() => {
    if (data?.findYourCity?.cities) {
      const allCities = data.findYourCity.cities.map((item: any) => item.city);
      setFilteredCards(allCities);
      setIsLoading(false);
    }
  }, [data]);
  useEffect(() => {
    setShowModal(false);
  }, [router.query.slug]);

  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);

  useEffect(() => {
    if (showModal && isNotDesktop) {
      const scrollY = window.scrollY;
      const initialHeight = window.innerHeight;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';

      const handleResize = () => {
        if (window.visualViewport) {
          const isKeyboard = window.visualViewport.height < initialHeight * 0.7;
          setIsKeyboardVisible(isKeyboard);
        }
      };

      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', handleResize);
      }

      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = 'auto';
        document.body.style.overflow = 'unset';
        if (window.visualViewport) {
          window.visualViewport.removeEventListener('resize', handleResize);
        }
        window.scrollTo(0, scrollY);
        setIsKeyboardVisible(false);
      };
    } else {
      setSearchItem('');
      setIsKeyboardVisible(false);
    }
  }, [showModal]);
  useEffect(() => {
    if (isNotDesktop) {
      setIsLoading(true);
      getData().then(() => setIsLoading(false));
    }
  }, []);
  useEffect(() => {
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);
  useEffect(() => {
    if (showModal && (!searchItem || searchItem.trim() === '')) {
      if (isNotDesktop) {
        setIsLoading(true);
        getData().then(() => {
          if (data?.findYourCity?.cities) {
            const allCities = data.findYourCity.cities.map(
              (item: any) => item.city
            );
            setFilteredCards(allCities);
          }
          setIsLoading(false);
        });
      } else {
        setFilteredCards(defaultCities);
      }
    }
  }, [showModal, searchItem]);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!showModal && !isNotDesktop) {
      inputRef.current?.blur();
    }
  }, [showModal, isNotDesktop]);
  useEffect(() => {
    if (!showModal || !isNotDesktop) return;

    const scrollY = window.scrollY;
    const body = document.body;

    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overflow = 'hidden';

    const preventTouch = (e: TouchEvent) => {
      if (
        e.target instanceof HTMLElement &&
        !e.target.closest('.modal-scroll')
      ) {
        e.preventDefault();
      }
    };
    document.addEventListener('touchmove', preventTouch, { passive: false });

    return () => {
      body.style.position = '';
      body.style.top = '';
      body.style.left = '';
      body.style.right = '';
      body.style.width = '';
      body.style.overflow = '';
      window.scrollTo(0, scrollY);
      document.removeEventListener('touchmove', preventTouch);
    };
  }, [showModal, isNotDesktop]);

  return (
    <>
      <div className={`flex w-full relative `}>
        <div className="cursor-pointer mx-auto xl:ml-0 px-2 h-full max-w-[440px] xl:max-w-none xl:h-[60px] items-center  gap-3 border border-[#625A5A] relative w-full xl:w-[90%] 2xl:w-[85%] bg-white text-white text-xl flex justify-start rounded-[40px]">
          <Image
            src="/city-location-icon.svg"
            width={23}
            height={10}
            alt="location icon"
            onClick={handleToggleModal}
            className="h-[14px] w-[14px] xl:h-[24px] xl:w-[24px] shrink-0"
          />
          <input
            ref={inputRef}
            className="input text-[16px] xl:text-[18px] text-black xl:px-2 rounded-md w-full py-1 focus:outline-none caret-transparent xl:caret-black"
            onChange={e => {
              setSearchItem(e.target.value);
            }}
            placeholder={`Search for${isMobile ? '' : ' any'} city`}
            onFocus={() => {
              if (!showModal) setShowModal(true);
              if (isNotDesktop) {
                (document.activeElement as HTMLElement)?.blur();
              }
            }}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              if (isNotDesktop) {
                handleToggleModal();
                (document.activeElement as HTMLElement)?.blur();
              }
            }}
            readOnly={isNotDesktop}
            value={searchItem || ''}
          />
          <Image
            src={
              !searchItem || searchItem.trim() === ''
                ? '/blue-magnifier.svg'
                : '/close-cross-circle.svg'
            }
            width={60}
            height={60}
            alt="search icon"
            onClick={handleToggleModal}
            className={`h-[24px] w-[24px] shrink-0 ${
              !searchItem || searchItem.trim() === ''
                ? 'xl:h-[44px] xl:w-[44px]'
                : 'xl:h-[26px] xl:w-[26px] mr-3'
            }`}
          />
        </div>
        {showModal && (
          <div
            onClick={e => e.stopPropagation()}
            onMouseLeave={() => {
              if (showModal && !isNotDesktop) {
                setShowModal(false);
              }
            }}
            className={`
            fixed inset-0 z-50 bg-white 
            rounded-none 
            flex flex-col 
            p-3
            overflow-y-auto
            ${isKeyboardVisible ? 'h-full mb-12' : 'h-[100dvh]'}
            xl:w-[90%] 2xl:w-[85%]
            xl:absolute xl:top-full xl:left-0 xl:right-0 xl:mt-1 
            xl:rounded-[40px] xl:h-auto 
            xl:overflow-visible xl:max-h-none
            xl:p-0 xl:bg-white
          `}
          >
            <div className=" w-[80%] mx-auto flex items-center justify-between my-4 xl:hidden z-[100]">
              <div className="text-[18px]   font-[500]  md:hidden  ">
                {' '}
                Begin Your Adventure
              </div>
              <div className="text-[18px]   font-[500]  hidden md:flex xl:hidden  ">
                {' '}
                Start your trip.
              </div>
              <Image
                src={'/close-cross.svg'}
                alt="close button"
                width={16}
                height={16}
                className="w-[16px] h-[16px]"
                onClick={handleToggleModal}
              />
            </div>
            <div
              onClick={e => {
                e.stopPropagation();
              }}
              className="xl:hidden px-2 h-[44px] xl:h-[68px] items-center  gap-3 border border-[#625A5A] relative w-full bg-white text-white z-[39]  md:py-4 text-xl flex justify-start rounded-[40px]"
            >
              <Image
                src="/city-location-icon.svg"
                width={23}
                height={10}
                alt="location icon"
                className="w-[14px] h-[14px] xl:w-[24px]"
              />
              <input
                className="input text-[16px] text-black xl:px-2 rounded-md w-full py-1 focus:outline-none"
                onChange={e => {
                  setSearchItem(e.target.value);
                }}
                placeholder="Search for any city"
                onClick={e => {
                  e.stopPropagation();
                }}
                value={searchItem || ''}
              />
              <Image
                src="/close-cross-circle.svg"
                width={60}
                height={60}
                alt="search icon"
                className="w-[18px] h-[18px] xl:w-[44px]"
                onClick={() => setSearchItem('')}
              />
            </div>
            <div
              className=" modal-content modal-scroll  bg-white w-full xl:rounded-t-[40px] xl:border xl:border-[#625A5A] xl:border-b-0 "
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              {isLoading ? (
                <div className="flex items-center justify-center m-2 p-2 w-[98%] min-h-[64px] rounded-[40px] gap-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                </div>
              ) : filteredCards && filteredCards?.length > 0 ? (
                filteredCards?.map(city => (
                  <Link href={`/${city?.slug}`} key={city?.id} target="_blank">
                    <div
                      className="flex items-center hover:bg-[#E0E0E0] m-2 p-2 w-[98%] min-h-[64px] rounded-lg xl:rounded-[40px] cursor-pointer gap-4 group"
                      onClick={handleToggleModal}
                    >
                      <div className="w-[44px] h-[44px] rounded-lg xl:rounded-full overflow-hidden shrink-0">
                        <Image
                          src={
                            city?.card_media?.[0]?.url ||
                            '/city-location-icon.svg'
                          }
                          width={60}
                          height={60}
                          alt="city image"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <p className="flex-grow">{city?.name}</p>
                      <span className="text-primary font-bold flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        See Tours
                        <Image
                          src="/right-arrow.svg"
                          width={60}
                          height={60}
                          alt="right arrow"
                          className="w-[11px] shrink-0"
                        />
                      </span>
                    </div>
                  </Link>
                ))
              ) : (
                <>
                  <div className="flex xl:hidden flex-col items-center m-2 mt-[30%] p-2 w-[98%] min-h-[64px] rounded-[40px] gap-2">
                    <Image
                      src="/city-location-icon.svg"
                      width={60}
                      height={60}
                      alt="location icon"
                      className="w-[42px] h-[52px] "
                    />
                    <p className="flex-grow text-center text-gray-500">
                      We haven&apos;t made it there - but we&apos;re always
                      expanding!
                    </p>
                    <div
                      onClick={() => {
                        router.push('/cities');
                      }}
                      className="md:hidden flex  rounded-[40px] justify-center items-center text-[18px] text-white font-[400] w-[205px] mx-auto bg-primary py-2"
                    >
                      Browse Our Cities
                    </div>
                  </div>{' '}
                  <div className="hidden xl:flex items-center m-2 p-2 w-[98%] min-h-[64px] rounded-[40px] gap-4">
                    <p className="flex-grow text-center text-gray-500 mt-8">
                      No cities found
                    </p>
                  </div>
                </>
              )}
            </div>
            <Link
              href="/cities"
              target="_blank"
              className="hidden xl:flex border border-[#625A5A] border-t border-t-[#E0E0E0] bg-white rounded-b-[40px]"
            >
              <div className="flex items-center hover:bg-[#E0E0E0] m-2 p-2 w-[98%] min-h-[64px] rounded-[40px] cursor-pointer gap-4">
                <Image
                  src="/all-cities-icon.svg"
                  width={60}
                  height={60}
                  alt="all cities icon"
                  className="w-[20px] shrink-0"
                />
                <p className="flex-grow">See all our Cities</p>
              </div>
            </Link>
          </div>
        )}
      </div>
    </>
  );
};

export default SearchModal;
